/**
 * Utilitaires pour les cartes de Tarot
 * 
 * Numérotation serveur Java:
 * - 1-14: Cœur (1=As, 14=Roi)
 * - 15-28: Pique (15=As, 28=Roi)
 * - 29-42: Carreau (29=As, 42=Roi)
 * - 43-56: Trèfle (43=As, 56=Roi)
 * - 57-77: Atouts 1-21
 * - 78: Excuse
 */

import { Suit, type CardNumber, type CardInfo } from '../types/game';

// ============================================
// Constantes
// ============================================

/** Noms des couleurs */
export const SUIT_NAMES = ['Cœur', 'Pique', 'Carreau', 'Trèfle', 'Atout'] as const;

/** Noms des valeurs (index 1-14) */
export const VALUE_NAMES = [
  '', 'As', '2', '3', '4', '5', '6', '7', '8', '9', '10', 'Valet', 'Cavalier', 'Dame', 'Roi'
] as const;

/** Points des cartes (par valeur, index 1-14) */
const VALUE_POINTS = [
  0, 0.5, 0.5, 0.5, 0.5, 0.5, 0.5, 0.5, 0.5, 0.5, 0.5, 1.5, 2.5, 3.5, 4.5
];

/** Points des atouts spéciaux */
const OUDLER_POINTS = 4.5;  // 1, 21, Excuse

// ============================================
// Fonctions de décodage
// ============================================

/**
 * Extrait la couleur d'une carte (0-4)
 */
export function getCardSuit(cardNumber: CardNumber): Suit {
  if (cardNumber < 1 || cardNumber > 78) {
    throw new Error(`Invalid card number: ${cardNumber}`);
  }
  
  if (cardNumber >= 57) {
    return Suit.Trumps;  // Atouts (57-77) et Excuse (78)
  }
  
  return Math.floor((cardNumber - 1) / 14) as Suit;
}

/**
 * Extrait la valeur d'une carte
 * - Pour les couleurs: 1-14 (As à Roi)
 * - Pour les atouts: 1-21
 * - Pour l'excuse: 22
 */
export function getCardValue(cardNumber: CardNumber): number {
  if (cardNumber < 1 || cardNumber > 78) {
    throw new Error(`Invalid card number: ${cardNumber}`);
  }
  
  if (cardNumber === 78) {
    return 22;  // Excuse
  }
  
  if (cardNumber >= 57) {
    return cardNumber - 56;  // Atouts 1-21
  }
  
  return ((cardNumber - 1) % 14) + 1;  // Couleurs 1-14
}

/**
 * Décode complètement une carte
 */
export function getCardInfo(cardNumber: CardNumber): CardInfo {
  const suit = getCardSuit(cardNumber);
  const value = getCardValue(cardNumber);
  const isExcuse = cardNumber === 78;
  const isTrump = suit === Suit.Trumps;
  
  // Bouts: Petit (1), 21, Excuse
  const isOudler = isExcuse || (isTrump && (value === 1 || value === 21));
  
  // Points
  let points: number;
  if (isOudler) {
    points = OUDLER_POINTS;
  } else if (isTrump) {
    points = 0.5;  // Atouts normaux
  } else {
    points = VALUE_POINTS[value] || 0.5;
  }
  
  return {
    number: cardNumber,
    suit,
    value,
    isExcuse,
    isTrump,
    isOudler,
    points,
  };
}

/**
 * Retourne le nom complet d'une carte
 */
export function getCardName(cardNumber: CardNumber): string {
  const info = getCardInfo(cardNumber);
  
  if (info.isExcuse) {
    return 'Excuse';
  }
  
  if (info.isTrump) {
    return `${info.value} d'Atout`;
  }
  
  const valueName = VALUE_NAMES[info.value] || `${info.value}`;
  const suitName = SUIT_NAMES[info.suit];
  return `${valueName} de ${suitName}`;
}

/**
 * Retourne le nom court d'une carte (pour affichage compact)
 */
export function getCardShortName(cardNumber: CardNumber): string {
  const info = getCardInfo(cardNumber);
  
  if (info.isExcuse) {
    return 'Exc';
  }
  
  if (info.isTrump) {
    return `${info.value}A`;
  }
  
  const suitSymbols = ['♥', '♠', '♦', '♣'];
  const valueSymbol = info.value === 1 ? 'A' : 
                      info.value === 11 ? 'V' :
                      info.value === 12 ? 'C' :
                      info.value === 13 ? 'D' :
                      info.value === 14 ? 'R' :
                      `${info.value}`;
  
  return `${valueSymbol}${suitSymbols[info.suit]}`;
}

/**
 * Retourne le nom court d'une carte pour les logs (format: 4C, RK, 10T, 1P, 12A, Exc)
 * C=Cœur, K=Carreau, T=Trèfle, P=Pique, A=Atout
 */
export function getCardLogName(cardNumber: CardNumber): string {
  const info = getCardInfo(cardNumber);
  
  if (info.isExcuse) {
    return 'Exc';
  }
  
  if (info.isTrump) {
    return `${info.value}A`;
  }
  
  // C=Cœur, P=Pique, K=Carreau, T=Trèfle
  const suitLetters = ['C', 'P', 'K', 'T'];
  const valueSymbol = info.value === 11 ? 'V' :
                      info.value === 12 ? 'C' :
                      info.value === 13 ? 'D' :
                      info.value === 14 ? 'R' :
                      `${info.value}`;
  
  return `${valueSymbol}${suitLetters[info.suit]}`;
}

// ============================================
// Chemins des images
// ============================================

/**
 * Retourne le chemin de l'image d'une carte
 */
export function getCardImagePath(cardNumber: CardNumber): string {
  return `/cards/${cardNumber}.png`;
}

/**
 * Retourne le chemin de l'image du dos de carte
 */
export function getCardBackImagePath(): string {
  return '/cards/back.png';
}

// ============================================
// Validation et tri
// ============================================

/**
 * Vérifie si un numéro de carte est valide
 */
export function isValidCard(cardNumber: CardNumber): boolean {
  return Number.isInteger(cardNumber) && cardNumber >= 1 && cardNumber <= 78;
}

/**
 * Compare deux cartes pour le tri (par couleur puis par valeur)
 */
export function compareCards(a: CardNumber, b: CardNumber): number {
  const infoA = getCardInfo(a);
  const infoB = getCardInfo(b);
  
  // D'abord par couleur
  if (infoA.suit !== infoB.suit) {
    return infoA.suit - infoB.suit;
  }
  
  // Puis par valeur
  return infoA.value - infoB.value;
}

/**
 * Trie une main de cartes
 */
export function sortHand(cards: CardNumber[]): CardNumber[] {
  return [...cards].sort(compareCards);
}

/**
 * Détermine la couleur demandée dans un pli.
 * 
 * Règle spéciale de l'Excuse : Si le premier joueur joue l'Excuse,
 * c'est la carte du deuxième joueur qui détermine la couleur demandée.
 * Si lui aussi joue l'Excuse, c'est le troisième, etc.
 * 
 * @returns La couleur demandée, ou undefined si le pli est vide ou ne contient que des Excuses
 */
export function getLeadSuit(trickCards: CardNumber[]): Suit | undefined {
  for (const card of trickCards) {
    const info = getCardInfo(card);
    // La première carte non-Excuse détermine la couleur demandée
    if (!info.isExcuse) {
      return info.suit;
    }
  }
  // Que des Excuses (cas très rare) ou pli vide
  return undefined;
}

/**
 * Vérifie si une carte est jouable selon les règles du tarot
 */
export function isPlayableCard(
  card: CardNumber,
  hand: CardNumber[],
  trickCards: CardNumber[],
  _leadSuit?: Suit
): boolean {
  void _leadSuit; // Paramètre conservé pour compatibilité API
  const cardInfo = getCardInfo(card);
  
  // Premier à jouer: tout est permis
  if (trickCards.length === 0) {
    return true;
  }
  
  // Déterminer la couleur demandée (en tenant compte de la règle de l'Excuse)
  const requestedSuit = getLeadSuit(trickCards);
  
  // L'excuse est toujours jouable
  if (cardInfo.isExcuse) {
    return true;
  }
  
  // Si que des Excuses ont été jouées jusqu'ici, tout est permis
  if (requestedSuit === undefined) {
    return true;
  }
  
  // Si on a la couleur demandée, on doit la jouer
  const hasSuit = hand.some(c => {
    const info = getCardInfo(c);
    return info.suit === requestedSuit && !info.isExcuse;
  });
  
  if (hasSuit) {
    if (cardInfo.suit !== requestedSuit) return false;
    // Atout demandé : monter si possible (aligné backend getLegalCards)
    if (requestedSuit === Suit.Trumps) {
      const highestTrumpPlayed = Math.max(
        0,
        ...trickCards
          .map(c => getCardInfo(c))
          .filter(info => info.isTrump && !info.isExcuse)
          .map(info => info.value),
      );
      const canOvertrump = hand.some(c => {
        const info = getCardInfo(c);
        return info.isTrump && !info.isExcuse && info.value > highestTrumpPlayed;
      });
      if (canOvertrump) return cardInfo.value > highestTrumpPlayed;
    }
    return true;
  }
  
  // Si on n'a pas la couleur demandée...
  if (requestedSuit === Suit.Trumps) {
    // Atout demandé mais on n'en a pas: on peut jouer n'importe quoi
    return true;
  }
  
  // Couleur demandée mais on n'en a pas: on doit couper si possible
  const hasTrumps = hand.some(c => {
    const info = getCardInfo(c);
    return info.isTrump && !info.isExcuse;
  });
  
  if (hasTrumps) {
    // On doit jouer un atout
    if (!cardInfo.isTrump) {
      return false;
    }
    
    // Et on doit monter si possible
    const highestTrumpPlayed = Math.max(
      ...trickCards
        .map(c => getCardInfo(c))
        .filter(info => info.isTrump && !info.isExcuse)
        .map(info => info.value),
      0
    );
    
    const canOvertrump = hand.some(c => {
      const info = getCardInfo(c);
      return info.isTrump && !info.isExcuse && info.value > highestTrumpPlayed;
    });
    
    if (canOvertrump) {
      return cardInfo.value > highestTrumpPlayed;
    }
    
    // Pas possible de monter: n'importe quel atout
    return true;
  }
  
  // Pas d'atout non plus: pisser
  return true;
}

/**
 * Retourne les cartes jouables dans une main
 */
export function getPlayableCards(
  hand: CardNumber[],
  trickCards: CardNumber[]
): CardNumber[] {
  return hand.filter(card => isPlayableCard(card, hand, trickCards));
}

// ============================================
// Utilitaires pour le comptage
// ============================================

/**
 * Compte les points dans un ensemble de cartes
 */
export function countPoints(cards: CardNumber[]): number {
  return cards.reduce((sum, card) => sum + getCardInfo(card).points, 0);
}

/**
 * Compte les bouts dans un ensemble de cartes
 */
export function countOudlers(cards: CardNumber[]): number {
  return cards.filter(card => getCardInfo(card).isOudler).length;
}

/**
 * Retourne le nombre de points requis pour gagner selon le nombre de bouts
 */
export function getRequiredPoints(oudlerCount: number): number {
  switch (oudlerCount) {
    case 0: return 56;
    case 1: return 51;
    case 2: return 41;
    case 3: return 36;
    default: return 56;
  }
}

// ============================================
// Génération de deck
// ============================================

/**
 * Génère un deck complet de 78 cartes
 */
export function generateDeck(): CardNumber[] {
  return Array.from({ length: 78 }, (_, i) => i + 1);
}

/**
 * Mélange un tableau (Fisher-Yates)
 */
export function shuffle<T>(array: T[]): T[] {
  const result = [...array];
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}

// ============================================
// ML text player name substitution
// ============================================

const POSITION_DIRECTION: Record<number, string> = {
  0: 'Sud', 1: 'Est', 2: 'Nord', 3: 'Ouest',
};

/**
 * Replaces generic "Joueur N" references in ML-generated text with
 * role-based labels: "Preneur", "Défenseur Nord", etc.
 *
 * Also corrects "partenaire" misuse when the referenced player
 * is not on the same side as `evaluatedSeat`.
 */
export function substitutePlayerNames(
  text: string,
  taker: number | null,
  evaluatedSeat: number,
  viewPosition: number = 0,
): string {
  if (!text) return text;

  let result = text;

  for (let pos = 0; pos < 4; pos++) {
    const dir = POSITION_DIRECTION[(pos - viewPosition + 4) % 4];
    const label = pos === taker ? 'Preneur' : `Défenseur ${dir}`;
    result = result.replace(new RegExp(`Joueur\\s*${pos}\\b`, 'g'), label);
    result = result.replace(new RegExp(`joueur\\s*${pos}\\b`, 'g'), label.toLowerCase());
  }

  // Fix "partenaire" misuse: only defenders share a partnership.
  // The taker has no partner in 4-player tarot.
  if (taker !== null) {
    const evaluatedIsAttack = evaluatedSeat === taker;
    if (evaluatedIsAttack) {
      result = result.replace(/\bmon partenaire\b/gi, 'un défenseur');
      result = result.replace(/\ble partenaire\b/gi, 'un défenseur');
      result = result.replace(/\bson partenaire\b/gi, 'un défenseur');
      result = result.replace(/\bmes partenaires\b/gi, 'les défenseurs');
      result = result.replace(/\bles partenaires\b/gi, 'les défenseurs');
      result = result.replace(/\bses partenaires\b/gi, 'les défenseurs');
      result = result.replace(/\bvos partenaires\b/gi, 'les défenseurs');
      result = result.replace(/\bvotre partenaire\b/gi, 'un défenseur');
      // Parenthetical annotation from ML signals: "(partenaire)" → "(adversaire)"
      result = result.replace(/\(partenaire\)/gi, '(adversaire)');
    }
  }

  return result;
}
